import React, { useEffect, useState } from 'react';

// === CONFIGURE CES ENDPOINTS ===
const CONSUMABLES_ENDPOINT = "https://script.google.com/macros/s/AKfycbx2CQ2Xgi4NZk-MDodg25MyGv5-J1mdZAYiY1tOUCwf6Jh4rjncohd-eXmAsK0Y_Dc/exec"; // GET
const SUBMIT_ENDPOINT = "https://inventory-app.protectioncivile-pariscentre.org/proxy.php"; // POST

export default function InventoryApp() {
  const [consumables, setConsumables] = useState([]);
  const [rows, setRows] = useState([{ id: Date.now(), item: '', qty: '', suggestions: [] }]);
  const [globalComment, setGlobalComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(CONSUMABLES_ENDPOINT);
        if (!res.ok) throw new Error('Impossible de charger la liste');
        const data = await res.json();
        setConsumables(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setConsumables([]);
      }
    }
    load();
  }, []);

  function addRow(afterId = null) {
    const newRow = { id: Date.now() + Math.random(), item: '', qty: '', suggestions: [] };
    if (afterId === null) {
      setRows(rs => [...rs, newRow]);
    } else {
      setRows(rs => {
        const i = rs.findIndex(r => r.id === afterId);
        const copy = rs.slice();
        copy.splice(i + 1, 0, newRow);
        return copy;
      });
    }
  }

  function removeRow(id) {
    setRows(rs => rs.filter(r => r.id !== id));
  }

  function updateRow(id, changes) {
    setRows(rs => rs.map(r => r.id === id ? { ...r, ...changes } : r));
  }

  function handleItemInput(id, value) {
    const filtered = value.length > 0 ? consumables.filter(c => c.toLowerCase().includes(value.toLowerCase())) : consumables.slice(0, 10);
    updateRow(id, { item: value, suggestions: filtered });
  }

  function pickSuggestion(id, value) {
    updateRow(id, { item: value, suggestions: [] });
  }

  function validateForm() {
    const validRows = rows.filter(r => r.item && String(r.qty).trim() !== '');
    if (validRows.length === 0) {
      setMessage({ type: 'error', text: "Ajoute au moins un élément avec quantité" });
      return false;
    }
    return true;
  }

  function mergeDuplicates(rows) {
    const map = {};
    rows.forEach(r => {
      if (!map[r.item]) {
        map[r.item] = { ...r, qty: Number(r.qty) };
      } else {
        map[r.item].qty += Number(r.qty);
      }
    });
    return Object.values(map);
  }

  async function handleSubmit() {
    if (!validateForm()) return;
    setLoading(true);
    setMessage(null);

    const mergedRows = mergeDuplicates(rows.filter(r => r.item && String(r.qty).trim() !== ''));

    const payload = {
      timestamp: new Date().toISOString(),
      rows: mergedRows.map(r => ({ item: r.item, qty: r.qty })),
      globalComment
    };

    try {
      const res = await fetch(SUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Erreur serveur');
      await res.json().catch(() => ({}));
      setMessage({ type: 'success', text: 'Liste envoyée ✅' });
      setRows([{ id: Date.now(), item: '', qty: '', suggestions: [] }]);
      setGlobalComment('');
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: "Échec de l'envoi — réessaie" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-[#024589] text-white p-4">
        <h1 className="text-xl font-bold">Protection Civile — Consommables</h1>
        <p className="text-sm opacity-90">Renseigne les consommations de fin de poste</p>
      </header>

      <main className="flex-1 p-4 space-y-4 pb-32">
        {rows.map((row) => (
          <div key={row.id} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F78D20]"
                  placeholder="Nom du matériel..."
                  value={row.item}
                  onChange={e => handleItemInput(row.id, e.target.value)}
                />
                {row.suggestions && row.suggestions.length > 0 && (
                  <ul className="absolute z-20 left-0 right-0 bg-white border mt-1 rounded-lg shadow-lg max-h-40 overflow-auto">
                    {row.suggestions.slice(0, 8).map(s => (
                      <li
                        key={s}
                        className="px-3 py-2 hover:bg-[#F78D20] hover:text-white cursor-pointer"
                        onClick={() => pickSuggestion(row.id, s)}>
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <input
                type="number"
                min="0"
                className="w-20 border rounded-lg px-2 py-2 text-center focus:outline-none focus:ring-2 focus:ring-[#F78D20]"
                value={row.qty}
                placeholder="Qté"
                onChange={e => updateRow(row.id, { qty: e.target.value })}
              />
            </div>

            <div className="mt-2 flex gap-2 justify-end">
              <button className="text-sm px-3 py-1 rounded-lg border hover:bg-gray-50" onClick={() => addRow(row.id)}>+ Ajouter</button>
              {rows.length > 1 && (
                <button className="text-sm px-3 py-1 rounded-lg border text-red-600 hover:bg-red-50" onClick={() => removeRow(row.id)}>Supprimer</button>
              )}
            </div>
          </div>
        ))}

        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200">
          <label className="text-sm font-semibold text-gray-700">Commentaire global (optionnel)</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-[#F78D20]"
            rows={3}
            value={globalComment}
            onChange={e => setGlobalComment(e.target.value)}
          />
        </div>

        {message && (
          <div className={`p-3 rounded-xl text-center font-medium ${message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
            {message.text}
          </div>
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3">
        <button
          className="flex-1 py-3 rounded-xl bg-[#024589] text-white font-semibold hover:bg-[#F78D20] transition disabled:opacity-50"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Envoi...' : 'Valider'}
        </button>
        <button
          className="px-4 py-3 rounded-xl border border-gray-300 font-medium hover:bg-gray-50"
          onClick={() => { setRows([{ id: Date.now(), item: '', qty: '', suggestions: [] }]); setGlobalComment(''); }}
        >
          Réinitialiser
        </button>
      </div>
    </div>
  );
}

